import { IMediaResponse } from "@/service/service";
import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "@talentech/components";

// DELETE /media/{filename} answers 404 when the blob is already gone — e.g. a
// stale gallery tile deleted moments earlier. Callers treat that as success.
export const isAlreadyDeletedError = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  (error as { status?: unknown }).status === 404;

export const mediaApi = createApi({
  reducerPath: "media",
  baseQuery: axiosBaseQuery({ baseUrl: process.env.API_URL as string }),
  tagTypes: ["Media"],
  endpoints: (builder) => ({
    getMedia: builder.query<IMediaResponse, Record<string, any>>({
      query: (params) => ({
        url: `/media/`,
        params,
      }),
      providesTags: ["Media"],
    }),
    deleteMedia: builder.mutation<void, string>({
      query: (filename) => ({
        url: `/media/${filename}`,
        method: "DELETE",
      }),
      // refetch the gallery even when the request fails — an error means the
      // grid was showing stale tiles and needs the fresh list anyway
      invalidatesTags: () => ["Media"],
      async onQueryStarted(filename, { dispatch, getState, queryFulfilled }) {
        // drop the tile from every cached gallery page immediately, so the
        // grid does not keep showing it while the slow list refetch runs
        const patches = mediaApi.util
          .selectCachedArgsForQuery(getState(), "getMedia")
          .map((args) =>
            dispatch(
              mediaApi.util.updateQueryData("getMedia", args, (draft) => {
                draft.files = draft.files.filter(
                  (file) => file.name !== filename
                );
              })
            )
          );
        try {
          await queryFulfilled;
        } catch (caught) {
          const error = (caught as { error?: unknown })?.error;
          // 404 = already deleted server-side; keep the tile removed
          if (!isAlreadyDeletedError(error)) {
            patches.forEach((patch) => patch.undo());
          }
        }
      },
    }),
  }),
});

export const {
  useGetMediaQuery,
  useLazyGetMediaQuery,
  useDeleteMediaMutation,
} = mediaApi;
